import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixStoresCatalogBrandV3BrandServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/stores/v3/brands',
        destPath: '/v3/brands',
      },
      {
        srcPath: '/stores/v3/bulk/brands',
        destPath: '/v3/bulk/brands',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/brand',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/brand',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/brand',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/brand',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/brand',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/brand',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_stores_brands-v-3';

/** Creates a brand. */
export function createBrand(payload: object): RequestOptionsFactory<any> {
  function __createBrand({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'brand.createdDate' }, { path: 'brand.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn: 'com.wix.stores.catalog.brand.v3.BrandService.CreateBrand',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'brand.createdDate' },
              { path: 'brand.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBrand;
}

/** Retrieves a brand. */
export function getBrand(payload: object): RequestOptionsFactory<any> {
  function __getBrand({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'GET' as any,
      methodFqn: 'com.wix.stores.catalog.brand.v3.BrandService.GetBrand',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands/{brandId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'brand.createdDate' },
              { path: 'brand.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBrand;
}

/**
 * Updates a brand.
 *
 * Each time the brand is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the brand.
 * This ensures you're working with the latest brand and prevents unintended overwrites.
 */
export function updateBrand(payload: object): RequestOptionsFactory<any> {
  function __updateBrand({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'brand.createdDate' }, { path: 'brand.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'PATCH' as any,
      methodFqn: 'com.wix.stores.catalog.brand.v3.BrandService.UpdateBrand',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands/{brand.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'brand.createdDate' },
              { path: 'brand.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBrand;
}

/**
 * Deletes a brand.
 *
 * > **Note:** Deleting a brand will also remove it from all products it is assigned to.
 */
export function deleteBrand(payload: object): RequestOptionsFactory<any> {
  function __deleteBrand({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'DELETE' as any,
      methodFqn: 'com.wix.stores.catalog.brand.v3.BrandService.DeleteBrand',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands/{brandId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBrand;
}

/**
 * Retrieves a list of up to 100 brands, given the provided filtering, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 *
 * Query Brands runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 */
export function queryBrands(payload: object): RequestOptionsFactory<any> {
  function __queryBrands({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'GET' as any,
      methodFqn: 'com.wix.stores.catalog.brand.v3.BrandService.QueryBrands',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'brands.createdDate' },
              { path: 'brands.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
            protoPath: '/v3/brands/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryBrands;
}

/** Creates multiple brands. */
export function bulkCreateBrands(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateBrands({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'brands.createdDate' }, { path: 'brands.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.brand.v3.BrandService.BulkCreateBrands',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/bulk/brands/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateBrands;
}

/**
 * Updates multiple brands.
 *
 * Each time a brand is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating a brand.
 * This ensures you're working with the latest brand and prevents unintended overwrites.
 */
export function bulkUpdateBrands(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateBrands({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'brands.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'brands.brand.createdDate' },
          { path: 'brands.brand.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.brand.v3.BrandService.BulkUpdateBrands',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/bulk/brands/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateBrands;
}

/** Retrieves a brand by name, or creates a brand if one with the passed `brandName` doesn't exist. */
export function getOrCreateBrand(payload: object): RequestOptionsFactory<any> {
  function __getOrCreateBrand({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.brand.v3.BrandService.GetOrCreateBrand',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/brands/get-or-create',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'brand.createdDate' },
              { path: 'brand.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOrCreateBrand;
}

/** Retrieves multiple brands by name, or creates multiple brands if those with the passed `brandNames` don't exist. */
export function bulkGetOrCreateBrands(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetOrCreateBrands({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.brand.v3.BrandService.BulkGetOrCreateBrands',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/bulk/brands/get-or-create',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetOrCreateBrands;
}

/** Deletes multiple brands. */
export function bulkDeleteBrands(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteBrands({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.brand',
      method: 'POST' as any,
      methodFqn:
        'com.wix.stores.catalog.brand.v3.BrandService.BulkDeleteBrands',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixStoresCatalogBrandV3BrandServiceUrl({
        protoPath: '/v3/bulk/brands/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteBrands;
}
